import { List } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { useDispatch, useSelector } from 'src/redux/store';
import {
    getMensagensWhatsappDb,
    setChatInfo,
    getChatsAtendimento,
    getChatsPendente,
    getChatsGeral,
    updateChatList,
} from 'src/redux/slices/chat';
import {
    getDivisoes,
    getListaArquivadas,
    desarquivarChat
} from 'src/redux/slices/chat-arquivo';
import useAuth from 'src/hooks/useAuth';
import { Button } from 'src/@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from 'src/@/components/ui/select';
import Iconify from 'src/components/Iconify';
import ChatAtendimentoItem from '../ChatsListItem/ChatAtendimentoItem';

// ----------------------------------------------------------------------

ChatConversationListArquivado.propTypes = {
    isOpenSidebar: PropTypes.bool
};

const TODAS = 'todas';

export default function ChatConversationListArquivado({
    isOpenSidebar,
    listaTagsReduce,
    listaCanaisReduce,
    tagsSelecionadas,
    canaisSelecionados,
    chatsNaoLidos
}) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation('chat');
    const { user } = useAuth();

    const { listaArquivadas, totalListaArquivadas, divisoes } = useSelector(
        (state) => state.chatArquivo
    );

    const [divisaoFiltro, setDivisaoFiltro] = useState(TODAS);

    useEffect(() => {
        if (!divisoes.length) dispatch(getDivisoes());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(
            getListaArquivadas({
                divisaoId: divisaoFiltro === TODAS ? null : divisaoFiltro
            })
        );
    }, [dispatch, divisaoFiltro]);

    // Mesmos filtros das demais abas (tag / canal / não lido), aplicados no
    // cliente sobre o que já veio da API.
    const applySortFilter = ({ tableData }) => {
        if (
            (!tagsSelecionadas || tagsSelecionadas.length === 0) &&
            (!canaisSelecionados || canaisSelecionados.length === 0) &&
            (!chatsNaoLidos || chatsNaoLidos.length === 0)
        ) {
            return tableData || [];
        }

        function mapStatus(status) {
            const statusMap = {
                'Chats não lidos': 'sim',
                'Chats lidos': 'nao',
                'Unread chats': 'sim',
                'Read chats': 'nao'
            };
            return statusMap[status] || status;
        }

        const chatsNaoLidoMapped = (chatsNaoLidos || []).map(mapStatus);
        let currentData = [...(tableData || [])];

        if (tagsSelecionadas && tagsSelecionadas.length > 0) {
            currentData = currentData.filter((item) =>
                (item.tags_ids || []).some((tag) => tagsSelecionadas.includes(tag.nome))
            );
        }

        if (canaisSelecionados && canaisSelecionados.length > 0) {
            currentData = currentData.filter((item) => canaisSelecionados.includes(item.nome_canal));
        }

        if (chatsNaoLidos && chatsNaoLidos.length > 0) {
            currentData = currentData.filter((item) => chatsNaoLidoMapped.includes(item.novaMensagem));
        }

        return currentData;
    };

    const dataFiltered = applySortFilter({ tableData: listaArquivadas });

    // Diferente do Finalizado, abre sempre: conversa arquivada pode não ter
    // direciona_usuarios nem direciona_setores (ex.: arquivada da aba "No bot"),
    // e o guard de direcionamento a tornaria impossível de abrir.
    const handleSelectConversation = (chat) => {
        if (!chat) return;
        dispatch(setChatInfo(chat));
        dispatch(getMensagensWhatsappDb(chat.sessao, chat.numeroCliente));
    };

    const handleDesarquivar = async (chat) => {
        try {
            const resposta = await dispatch(desarquivarChat(`${chat._id}`));
            const nomeExibido = chat.nomeCliente || t('archive.list.defaultContactName', 'Conversa');
            const destino = resposta?.status_destino || t('archive.list.defaultDestination', 'Atendimentos');
            const momento = resposta?.desarquivado_em || new Date().toISOString();

            dispatch(updateChatList({
                _id: chat._id,
                scope: { desarquivado_em: momento },
            }));

            // Garante a tag na listagem de destino mesmo se o socket atrasar.
            if (destino === 'Atendendo') {
                dispatch(getChatsAtendimento(user.clienteId, user.id, user.setorId, 100, true));
            } else if (destino === 'Pendente') {
                dispatch(getChatsPendente(user.clienteId, user.id, user.setorId, 10, true));
            } else {
                dispatch(getChatsGeral(user.clienteId, user.id, user.setorId, 10, true));
            }

            enqueueSnackbar(
                t('archive.list.unarchived', '{{name}} voltou para {{destination}}', { name: nomeExibido, destination: destino }),
                { variant: 'success', anchorOrigin: { vertical: 'bottom', horizontal: 'left' } }
            );
            dispatch(getDivisoes());
        } catch (error) {
            enqueueSnackbar(error?.mensagem || t('archive.list.errorUnarchive', 'Não foi possível desarquivar a conversa'), {
                variant: error?.conflito ? 'warning' : 'error',
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            });
            if (error?.conflito) {
                dispatch(
                    getListaArquivadas({
                        divisaoId: divisaoFiltro === TODAS ? null : divisaoFiltro
                    })
                );
            }
        }
    };

    const carregarMais = () => {
        dispatch(
            getListaArquivadas({
                divisaoId: divisaoFiltro === TODAS ? null : divisaoFiltro,
                offset: listaArquivadas.length
            })
        );
    };

    return (
        <>
            <div className="px-3 pt-2 pb-1">
                <Select value={divisaoFiltro} onValueChange={setDivisaoFiltro}>
                    <SelectTrigger className="h-8 text-xs border-gray-200 focus:ring-[#7C3AED]/20">
                        <SelectValue placeholder={t('archive.list.allDivisions', 'Todas as divisões')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={TODAS}>{t('archive.list.allDivisions', 'Todas as divisões')}</SelectItem>
                        {divisoes.map((divisao) => (
                            <SelectItem key={`${divisao._id}`} value={`${divisao._id}`}>
                                {divisao.nome}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {!dataFiltered.length ? (
                <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                    <Iconify icon="eva:archive-outline" width={28} className="text-gray-300 mb-2" />
                    <p className="text-sm font-medium text-gray-500">{t('archive.list.empty', 'Nenhuma conversa arquivada')}</p>
                    <p className="text-xs text-gray-400 mt-1">
                        {t('archive.list.emptyHint', 'Use "Arquivar em" no menu de uma conversa para começar.')}
                    </p>
                </div>
            ) : (
                <List>
                    {dataFiltered.map((result) => (
                        <ChatAtendimentoItem
                            key={result._id}
                            onSelectConversation={() => handleSelectConversation(result)}
                            onAceitarChat={() => handleDesarquivar(result)}
                            isOpenSidebar={isOpenSidebar}
                            chat={result}
                        />
                    ))}

                    {listaArquivadas.length < totalListaArquivadas && (
                        <Button
                            className="w-full bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
                            onClick={carregarMais}
                        >
                            {t('list.loadMore')} ({listaArquivadas.length}/{totalListaArquivadas})
                        </Button>
                    )}
                </List>
            )}
        </>
    );
}
